// The ticket conversation, oldest first.
//
// THE LOAD-BEARING PART OF THIS SCREEN (SPEC.md §7.4, docs/API.md §3.2).
//
// Collaborators — hardware and implementation staff — can email customers directly.
// When they could not, an internal note was structurally incapable of reaching a
// customer; now it is one mis-click away, and the only remaining control is that the
// two kinds of message never look remotely alike. So an internal note gets its own
// background, its own border, and an explicit "staff only" label on every single one,
// not just the first — a thread scrolled halfway must still be readable at a glance.
//
// `is_internal_note` is trusted over `direction`, and either one saying "internal" is
// enough: mislabelling a customer-facing reply as internal is a cosmetic error,
// mislabelling an internal note as customer-facing is the failure mode that matters.

import { useState } from 'react';

import { deliveryLine } from './delivery.js';
import {
  attachmentHref, attachmentInlineHref, inlineKind, previewHref,
  formatDateTime, formatBytes, messageBodies, personName,
} from './format.js';
import TabularPreview from './TabularPreview.jsx';

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
      <path d="M8 1a3.5 3.5 0 0 0-3.5 3.5V6H4a1.5 1.5 0 0 0-1.5 1.5v5A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 12 6h-.5V4.5A3.5 3.5 0 0 0 8 1Zm2 5H6V4.5a2 2 0 1 1 4 0V6Z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
      <path d="M2 4a1.5 1.5 0 0 1 1.5-1.5h9A1.5 1.5 0 0 1 14 4v.28L8 7.9 2 4.28V4Zm0 2.03V12a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 14 12V6.03L8.39 9.42a.75.75 0 0 1-.78 0L2 6.03Z" />
    </svg>
  );
}

// Every colour below is a semantic token from src/styles/app.css. The note set
// (bg-note / bg-note-strong / border-note-line / bg-note-accent / text-note-*) is the
// only warm hue in the system and it is deliberately LOUDER in dark mode, where every
// untreated neutral collapses toward the same grey — see RULE 3 in app.css and
// SPEC.md §7.4. Nothing here picks between two greys to tell a note from a reply:
//
//   internal note     amber-brown card body, 8px amber-500 bar, amber header band,
//                     amber ring (2px in dark), lock chip, "Staff only" caption
//   customer-facing   ordinary surface, blue bar, blue-tinted header band, mail chip
//
// The separation is hue first and brightness second, so it survives greyscale and
// colour-blind rendering as well as a half-scrolled thread.
const INTERNAL = {
  // ring-2 in dark: a hairline that reads as an edge against white needs real weight
  // against a near-black page (app.css RULE 3).
  card: 'border-l-8 border-note-accent bg-note ring-1 ring-note-line dark:ring-2',
  header: 'bg-note-strong text-note-ink',
  chip: 'bg-note-accent text-note-on-accent ring-note-on-accent/25',
  chipText: 'Internal note',
  caption: 'Staff only, never sent to the customer',
  captionClass: 'text-note-ink',
  body: 'text-note-body',
  // Note metadata stays warm rather than dropping to the neutral muted ink, so no part
  // of a note ever renders in the reply palette.
  muted: 'text-note-ink',
  divider: 'border-note-line',
  icon: LockIcon,
};

const OUTBOUND = {
  card: 'border-l-8 border-reply-accent bg-reply ring-1 ring-line',
  header: 'bg-reply-strong text-reply-ink',
  // brand-600/white is 5.2:1 and identical in both themes (app.css reasons this out for
  // btn-primary); the fill also clears 3:1 against both card surfaces.
  chip: 'bg-brand-600 text-white ring-white/25',
  chipText: 'Customer-facing reply',
  caption: 'Emailed to the customer',
  captionClass: 'text-ink-muted',
  body: 'text-ink',
  muted: 'text-ink-muted',
  divider: 'border-line',
  icon: MailIcon,
};

const INBOUND = {
  // A neutral chip fill has no semantic token — slate-600 is invisible against a dark
  // card (2.2:1), so dark lifts it to slate-500 (3.6:1 on the card, 4.8:1 under white).
  card: 'border-l-8 border-line-strong bg-surface ring-1 ring-line',
  header: 'bg-sunken text-ink-secondary',
  chip: 'bg-slate-600 dark:bg-slate-500 text-white ring-white/25',
  chipText: 'From the customer',
  caption: 'Received by email',
  captionClass: 'text-ink-muted',
  body: 'text-ink',
  muted: 'text-ink-muted',
  divider: 'border-line',
  icon: MailIcon,
};

function styleFor(message) {
  if (message.is_internal_note || message.direction === 'internal') return INTERNAL;
  return message.direction === 'inbound' ? INBOUND : OUTBOUND;
}

function AuthorLine({ message }) {
  const name = personName(message.author);
  if (!name) {
    // author_type 'system' carries a null author (docs/API.md §3.2) — email ingestion
    // and automations have no person behind them.
    return <span className="font-medium">System</span>;
  }
  return (
    <span>
      <span className="font-medium">{name}</span>
      {/* A ticket's requester can be one of OUR OWN -- staff raising a ticket for a
          store -- which the desk allows and the import copies faithfully, so a customers
          row exists for them. Calling that person a customer in their own colleague's
          ticket is simply wrong, and `is_staff` is the server saying their address
          matches a user account. 2 of the 100 most recent tickets, so this is a label
          problem and not a model one: author_type stays 'customer' and every rule, view
          and trigger reading it keeps its meaning. */}
      {message.author_type === 'customer' && (
        <span className="ml-1 text-xs opacity-75">
          {message.author?.is_staff ? '(staff, raised this ticket)' : '(customer)'}
        </span>
      )}
    </span>
  );
}

/**
 * The images and video on a message, shown rather than described.
 *
 * WHY THIS SITS BESIDE THE LIST AND NOT INSTEAD OF IT. The list below is the record —
 * it names every attachment, says how big it is, and marks the ones retention has
 * purged. This is a convenience on top of it, so an agent reads the screenshot a
 * customer sent without a round trip through their Downloads folder. Every image here
 * also appears there, and nothing is only visible here.
 *
 * WHY THE BODY IS STILL TEXT. A customer's HTML is never handed to
 * `dangerouslySetInnerHTML` (format.js), so `<img src="cid:…">` inside the body cannot
 * resolve and inline images would otherwise be invisible entirely. Rendering them here
 * gets the picture in front of the agent without opening this origin to a mail client's
 * markup — which, for a desk whose session cookie is the thing worth stealing, is a
 * trade worth making explicitly.
 *
 * FAILURE IS EXPECTED AND SILENT. `attachmentInlineHref` asks based on the sender's
 * declared type, and the server answers on the actual bytes, so a script named
 * `screenshot.png` produces a 404 here BY DESIGN. `onError` drops that one image and
 * the file stays in the list below as an ordinary download. A broken-image icon would
 * be reporting a fault to the agent when the system is working correctly.
 */
function MediaPreviews({ attachments, publicRef }) {
  const [failed, setFailed] = useState(() => new Set());

  const shown = attachments
    .map((attachment) => ({
      attachment,
      src: attachmentInlineHref(publicRef, attachment),
      kind: inlineKind(attachment),
    }))
    .filter(({ attachment, src }) => src !== null && !failed.has(attachment.id));

  if (shown.length === 0) return null;
  const drop = (id) => setFailed((previous) => new Set(previous).add(id));

  return (
    <div className="mt-3 flex flex-wrap items-start gap-2">
      {shown.map(({ attachment, src, kind }) => (kind === 'video' ? (
        // NO WRAPPING LINK ON A VIDEO. The controls are the interaction — a click on
        // the picture means play/pause, and an anchor around it would swallow that and
        // navigate away mid-clip instead.
        //
        // `preload="metadata"` and not `auto`: the thread re-polls every 20 seconds, and
        // `auto` invites the browser to pull the whole file down before anybody presses
        // play. `metadata` is enough for the duration and the first frame, which is all
        // a stopped player shows.
        //
        // Playback survives the poll because this element keeps its key and its position
        // in the tree, so React updates it rather than remounting — a remount would
        // restart the video from zero every twenty seconds, which would be a far worse
        // bug than no player at all.
        <video
          key={attachment.id}
          src={src}
          controls
          preload="metadata"
          onError={() => drop(attachment.id)}
          className="block max-h-80 max-w-full rounded border border-line bg-black"
        >
          {/* Reached only by a browser with no <video> support at all. The download
              link in the list below is the real fallback; this is the accessible name
              for the element itself. */}
          {attachment.filename}
        </video>
      ) : (
        // Opens the image full size in its own tab. Safe to navigate to because the
        // server serves it with a type IT determined from the bytes, plus `nosniff` and
        // `default-src 'none'; sandbox` — the tab case is exactly what that CSP is for.
        // `rel` is not optional with `target="_blank"`: without `noopener` the new tab
        // gets a handle on this one through `window.opener`.
        <a
          key={attachment.id}
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded border border-line focus-visible:outline-2
                     focus-visible:outline-offset-2 focus-visible:outline-brand-600
                     dark:focus-visible:outline-brand-400"
        >
          <img
            src={src}
            // The filename is sender-chosen text; React escapes it, and it is the only
            // description of this picture that exists. A blank alt would leave a screen
            // reader with nothing at all.
            alt={attachment.filename}
            // `lazy` matters more here than it looks: the queue re-polls every 20
            // seconds and a long thread can carry a lot of screenshots.
            loading="lazy"
            onError={() => drop(attachment.id)}
            // Bounded, not stretched. `max-h` keeps a full-page screenshot from pushing
            // the rest of the thread off screen, and because only maxima are set, a
            // small image — a signature logo, which is most inline images in business
            // mail — renders at its own size instead of being blown up.
            className="block max-h-64 max-w-full rounded object-contain"
          />
        </a>
      )))}
    </div>
  );
}

function Attachments({ attachments, style, publicRef }) {
  if (attachments.length === 0) return null;
  return (
    <>
      <MediaPreviews attachments={attachments} publicRef={publicRef} />
      <AttachmentList attachments={attachments} style={style} publicRef={publicRef} />
    </>
  );
}

// `style` is the message's own palette: the divider and the de-emphasised text stay in
// the card's hue so an attachment row on a note never renders in neutral grey.
//
// THREE STATES, and they are visually distinct on purpose:
//
//   stored     — an underlined link that downloads the file.
//   not stored — the filename as plain text, exactly as it rendered before this
//                existed. This is what an attachment looks like between the email
//                arriving and the `mail.attachments` step fetching its bytes, and it
//                is what EVERY attachment looks like on a server with no attachment
//                store configured, which is today's default. No spinner and no "soon":
//                the bytes may never arrive (the mailbox copy can be deleted), and a
//                promise the system cannot keep is worse than a filename.
//   purged     — plain text plus the tombstone chip below.
//
// `is_downloadable` decides, via `attachmentHref`. Never `!is_purged`: that would turn
// every not-yet-fetched attachment into a link that 404s.
//
// A BUTTON ON SOME ROWS: "View as table" for a delimited file, which opens the
// table under the row (./TabularPreview.jsx). It is offered only where `previewHref`
// says the server would answer one, so it is never a button that only ever 415s, and it
// never replaces the download — the file stays a file, and the preview is bounded.
function AttachmentList({ attachments, style, publicRef }) {
  // Which previews are open, by attachment id. A Set rather than a single id, so a
  // message carrying two CSVs can have both tables open and compared side by side.
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen((previous) => {
    const next = new Set(previous);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  return (
    <ul className={`mt-3 space-y-1 border-t pt-2 text-xs ${style.divider}`}>
      {attachments.map((attachment) => {
        const href = attachmentHref(publicRef, attachment);
        const previewable = previewHref(publicRef, attachment) !== null;
        const shown = open.has(attachment.id);
        const panelId = `attachment-preview-${attachment.id}`;
        return (
          <li key={attachment.id} className="text-ink-secondary">
            <div className="flex items-center gap-2">
              {/* `download` asks the browser to save rather than navigate. The server
                  sends `Content-Disposition: attachment` and `X-Content-Type-Options:
                  nosniff` regardless — this attribute is a convenience, never the
                  control, because the filename is sender-chosen and a client-side hint
                  is not where a rendering decision about untrusted bytes belongs.

                  No `target=\"_blank\"`: a download does not need a tab, and one opened
                  for a response that never renders leaves a blank window behind. */}
              {href ? (
                <a
                  href={href}
                  download={attachment.filename}
                  className="font-medium text-brand-700 underline underline-offset-2 hover:no-underline dark:text-brand-300"
                >
                  {attachment.filename}
                </a>
              ) : (
                <span className="font-medium">{attachment.filename}</span>
              )}
              {formatBytes(attachment.size_bytes) && (
                <span className={style.muted}>{formatBytes(attachment.size_bytes)}</span>
              )}
              {/* Retention tombstone (SPEC.md §7.2): the row stays so the record shows an
                  attachment existed, and its bytes are gone from the store on purpose.

                  bg-ink/10 rather than bg-sunken: this chip sits on whichever card surface
                  the message uses, and a fixed neutral fill would drop a cool grey block
                  into an amber note. A tint of the ink token darkens light cards and
                  lightens dark ones, so it reads the same everywhere. */}
              {attachment.is_purged && (
                <span className={`rounded bg-ink/10 px-1.5 py-0.5 ${style.muted}`}>
                  purged by retention
                </span>
              )}
              {/* Last on the row, after the size and the tombstone, so the filename stays
                  the thing an agent's eye lands on. */}
              {previewable && (
                <button
                  type="button"
                  onClick={() => toggle(attachment.id)}
                  aria-expanded={shown}
                  aria-controls={panelId}
                  className="rounded border border-line px-1.5 py-0.5 font-medium
                             text-brand-700 hover:bg-ink/5 dark:text-brand-300
                             focus-visible:outline-2 focus-visible:outline-offset-2
                             focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
                >
                  {shown ? 'Hide table' : 'View as table'}
                </button>
              )}
            </div>
            {/* Mounted only while open, which is also what fetches it: nothing is
                requested for a file nobody asked to read. */}
            {shown && (
              <div id={panelId}>
                <TabularPreview publicRef={publicRef} attachment={attachment} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ThreadMessage({ message, publicRef }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const style = styleFor(message);
  const Icon = style.icon;
  const { display, full, hasQuoted } = messageBodies(message);
  const line = deliveryLine(message, formatDateTime);

  return (
    <article className={`overflow-hidden rounded-lg ${style.card}`}>
      <header className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 ${style.header}`}>
        <span
          className={`badge uppercase tracking-wide ring-inset ${style.chip}`}
          title={style.caption}
        >
          <Icon />
          {style.chipText}
        </span>
        <span className="text-sm">
          <AuthorLine message={message} />
        </span>
        <span className="ml-auto text-xs opacity-80" title={message.created_at}>
          {formatDateTime(message.created_at)}
        </span>
      </header>

      <p className={`px-4 pt-2 text-xs font-medium ${style.captionClass}`}>{style.caption}</p>

      <div className="px-4 pb-4 pt-2">
        <div className={`whitespace-pre-wrap break-words text-sm ${style.body}`}>
          {showQuoted ? full : display}
          {!display && <span className={`italic ${style.muted}`}>(no message body)</span>}
        </div>

        {/* body_stripped is the quote-trimmed display copy; body_html is the untrimmed
            original this expands to (docs/API.md §3.2). */}
        {hasQuoted && (
          <button
            type="button"
            className="mt-2 text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline"
            onClick={() => setShowQuoted((shown) => !shown)}
          >
            {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
          </button>
        )}

        {/* Mentions only exist on internal notes, so this always sits on the note card. */}
        {message.mentions?.length > 0 && (
          <p className="mt-3 text-xs text-note-ink">
            Notified: {message.mentions.map((mention) => mention.display_name).join(', ')}
          </p>
        )}

        <Attachments attachments={message.attachments ?? []} style={style} publicRef={publicRef} />

        {/* `sent_at` is delivery confirmation, never "was this posted" — the message is
            posted the moment it appears here, and `created_at` is what says when.

            IT NO LONGER SAYS "Queued for delivery", and that is a correction rather
            than a wording change. Graph `sendMail` is wired now (docs/API.md §3.5), and
            it is attempted ONCE, inline, when the reply is saved — nothing scans for
            unsent messages and nothing retries. So a null `sent_at` on an outbound
            message does not mean the mail is waiting in a queue; it means the mail did
            not go, and saying otherwise invited an agent to wait for a delivery that
            was never coming.

            AND IT NO LONGER SAYS "Not emailed" ABOUT MAIL THAT MAY HAVE GONE. The
            choice is `deliveryLine`'s (./delivery.js), which is where the argument for
            each outcome is written down and where the suite can reach it. What is here
            is only the paint: the `warning` tone is a bordered amber block rather than
            a muted grey line, because the muted grey line is exactly what an agent
            skims on the way to clicking send a second time. Amber and not red — this
            is "stop and check", not "this failed". Explicit colours rather than
            tokens, on the precedent of ReplyComposer's error box: the shared vocabulary
            in app.css has no warning set, and inventing one for a single site is how a
            vocabulary stops being one. red-50/amber-800 reads 6.9:1 in light,
            amber-950/amber-200 9.6:1 in dark. */}
        {line && (
          line.tone === 'warning' ? (
            <p
              className="mt-3 rounded-md border px-3 py-2 text-xs font-medium
                         border-amber-400 bg-amber-50 text-amber-900
                         dark:border-amber-600 dark:bg-amber-950 dark:text-amber-200"
            >
              {line.text}
            </p>
          ) : (
            <p className="mt-3 text-xs text-ink-muted">{line.text}</p>
          )
        )}
      </div>
    </article>
  );
}

export default function MessageThread({ messages, publicRef }) {
  if (messages.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-line-strong bg-surface px-4 py-10 text-center text-sm text-ink-muted">
        No messages on this ticket yet.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* A legend, because "obvious once you know" is not the same as obvious. */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-ink-muted">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded-sm border-l-4 border-note-accent bg-note ring-1 ring-note-line" />
          Internal note: staff only
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded-sm border-l-4 border-reply-accent bg-reply ring-1 ring-line" />
          Customer-facing
        </span>
      </div>

      {messages.map((message) => (
        <ThreadMessage key={message.id} message={message} publicRef={publicRef} />
      ))}
    </div>
  );
}
